import React from 'react';
import { ExternalLink, Github } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import sunnyside from '@/images/sunnyside.png';
import blogr from '@/images/blogr.png';
import media from '@/images/media.png';
import funeral from '@/images/funeral.png';
import admin from '@/images/admin.png';

const projects = [
  {
    id: 1,
    title: 'Store CMS',
    description:
      'A custom content management system that can be linked to a front end website. The system allows you to add different product along with the details and images. The system generates the api links automatically.',
    tech: ['NextJs', 'TailwindCSS', 'SQL', 'Cloudinary', 'AuthJS'],
    githubLink: 'https://github.com/warjonas/rental_cms',
    liveLink: 'https://skywalkersoftware-cms.vercel.app',
    image:
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    gridClass: 'md:col-span-2 md:row-span-2',
  },
  {
    id: 2,
    title: 'Funeral Home Admin System',
    description:
      'An admin panel for a local funeral parlor. The admin panel allows for the management of clients, tracking of expenses, and to manage multiple other details about the business. It also hooks into the main customer facing site where clients are able to complete forms for the funeral program details.',
    tech: ['NextJs', 'TailwindCSS', 'MongoDB', 'Auth0'],
    githubLink: 'https://github.com/warjonas/Fortuin-Funeral-Home',
    liveLink: 'https://ffh-admin-panel-fo6uxofn3-warjonas-projects.vercel.app',
    image: admin,
    gridClass: 'md:col-span-1 md:row-span-2',
  },
  {
    id: 3,
    title: 'Blogr',
    description:
      'Blog page example promoting the use of their content editing platform for publishing and creating efficient content.',
    tech: ['NextJS', 'TailwindCSS'],
    githubLink: 'https://github.com/username/blogr',
    liveLink: 'https://blogr-demo.example.com',
    image: blogr,
    gridClass: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 4,
    title: '1820 Media and Entertainment',
    description:
      'A single page website for an Events management company based in South Africa, Port Elizabeth. Showcasing their services and also providing contact form for inquiries.',
    tech: ['NextJS', 'TailwindCSS'],
    githubLink: 'https://github.com/username/1820-media',
    liveLink: 'https://1820media.example.com',
    image: media,
    gridClass: 'md:col-span-2 md:row-span-1',
  },
  {
    id: 5,
    title: 'Fortuin Funeral Home',
    description:
      'A website for a funeral home based in South Africa. It showcases all the services offered and also provides users with a contact page to choose their funeral plan option and request a call back.',
    tech: ['NextJS', 'TypeScript', 'TailwindCSS'],
    githubLink: 'https://github.com/username/fortuin-funeral',
    liveLink: 'https://fortuin-funeral.example.com',
    image: funeral,

    gridClass: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 6,
    title: 'Sunny Side Agency',
    description:
      'A creative single page landing website for an advertisement agency showcasing their services, client testimonials with the option of later adding a contact form.',
    tech: ['NextJS', 'TailwindCSS'],
    githubLink: 'https://github.com/warjonas/sunnyside',
    liveLink: 'https://sunnyside-dev-agency.vercel.app/',
    image: sunnyside,
    gridClass: 'md:col-span-2 md:row-span-1',
  },
];

const Projects = () => {
  const [titleRef, titleVisible] = useScrollAnimation(0.2);
  const [gridRef, gridVisible] = useScrollAnimation(0.1);

  return (
    <section className="py-24 px-4 md:px-8 relative">
      <div className="max-w-7xl mx-auto">
        <h2
          ref={titleRef}
          className={`text-5xl md:text-6xl font-bold mb-16 text-center scroll-animate ${titleVisible ? 'visible' : ''}`}
        >
          <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Featured Work
          </span>
        </h2>

        {/* Bento Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 scroll-animate ${gridVisible ? 'visible' : ''}`}
        >
          {projects.map((project) => (
            <div
              key={project.id}
              className={`group relative overflow-hidden rounded-2xl bg-zinc-900/40 backdrop-blur-sm border border-zinc-800 hover:border-purple-500/50 transition-all duration-500 hover:shadow-2xl hover:shadow-purple-500/20 ${project.gridClass} flex flex-col`}
            >
              {/* Glow effect on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/0 via-purple-600/0 to-cyan-500/0 group-hover:from-purple-600/10 group-hover:via-purple-500/5 group-hover:to-cyan-500/10 transition-all duration-500"></div>

              {/* Image Preview */}
              <div className="relative h-48 overflow-hidden flex-shrink-0">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-zinc-900/50 to-zinc-900/90"></div>
              </div>

              <div className="relative flex-1 p-6 flex flex-col">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-zinc-100 group-hover:text-purple-300 transition-colors">
                      {project.title}
                    </h3>
                  </div>

                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((tech, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs rounded-full bg-zinc-800/50 border border-zinc-700 text-zinc-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-auto">
                  <a
                    href={project.githubLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-zinc-800/50 border border-zinc-700 hover:border-purple-500/50 hover:bg-zinc-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
                  >
                    <Github className="w-4 h-4 text-zinc-400" />
                    <span className="text-xs font-medium text-zinc-300">
                      Code
                    </span>
                  </a>
                  <a
                    href={project.liveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:border-purple-500/50 hover:from-purple-600/30 hover:to-cyan-600/30 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20"
                  >
                    <ExternalLink className="w-4 h-4 text-cyan-400" />
                    <span className="text-xs font-medium text-cyan-300">
                      Live Demo
                    </span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
